/**
 * Reminder Service - سرویس یادآوری
 * این سرویس مسئول ارسال نوتیفیکیشن‌های یادآوری است
 */

import { pushService } from '../push/pushService';

class ReminderService {
  constructor() {
    this.scheduledNotifications = new Map();
  }

  /**
   * Send routine reminder
   * ارسال یادآوری روتین مراقبت پوست
   * 
   * @param {string} routineTime - زمان روتین (صبح/شب)
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async sendRoutineReminder(routineTime = 'صبح', userId = null) {
    try {
      console.log(`🌅 Sending routine reminder for ${routineTime}...`);
      
      const emoji = routineTime === 'صبح' ? '🌅' : '🌙';
      const timeText = routineTime === 'صبح' ? 'صبح' : 'شب';
      
      const result = await pushService.showLocalNotification(
        `${emoji} یادآوری روتین ${timeText}`,
        {
          body: `زمان روتین مراقبت پوست ${timeText} شما فرا رسیده است. برای پوستی سالم، روتین خود را انجام دهید!`,
          data: {
            type: 'routine-reminder',
            url: '/routine',
            routineTime,
            userId,
            timestamp: Date.now(),
          },
          actions: [
            { action: 'open', title: 'مشاهده روتین', icon: '/logo_pwa.png' },
            { action: 'done', title: 'انجام شد' },
            { action: 'dismiss', title: 'بعداً' },
          ],
        },
        'routine'
      );
      
      if (result) {
        console.log('✅ Routine reminder sent');
        this.trackNotification('routine', userId, routineTime);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Failed to send routine reminder:', error);
      return false;
    }
  }

  /**
   * Schedule daily routine reminders
   * برنامه‌ریزی یادآوری روزانه روتین
   * 
   * @param {Object} times - زمان‌های روتین { morning: '08:00', evening: '20:00' }
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<Array>} - آرایه شناسه‌های notification
   */
  async scheduleDailyRoutine(times = { morning: '08:00', evening: '20:00' }, userId = null) {
    try {
      console.log('⏰ Scheduling daily routine reminders...');
      
      const notifications = [];
      
      // Morning reminder
      if (times.morning) {
        const morningDelay = this.calculateDelayUntilTime(times.morning);
        const morningId = await this.scheduleRoutineReminder('صبح', morningDelay, userId);
        if (morningId) {
          notifications.push(morningId);
        }
      }
      
      // Evening reminder
      if (times.evening) {
        const eveningDelay = this.calculateDelayUntilTime(times.evening);
        const eveningId = await this.scheduleRoutineReminder('شب', eveningDelay, userId);
        if (eveningId) {
          notifications.push(eveningId);
        }
      }
      
      console.log(`✅ Scheduled ${notifications.length} daily routine reminders`);
      return notifications;
    } catch (error) {
      console.error('❌ Failed to schedule daily routine:', error);
      return [];
    }
  }

  /**
   * Schedule a routine reminder
   * برنامه‌ریزی یک یادآوری روتین
   * 
   * @param {string} routineTime - زمان روتین (صبح/شب)
   * @param {number} delay - تأخیر به میلی‌ثانیه
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<number>} - شناسه notification برنامه‌ریزی شده
   */
  async scheduleRoutineReminder(routineTime, delay, userId = null) {
    try {
      const emoji = routineTime === 'صبح' ? '🌅' : '🌙';
      const timeText = routineTime === 'صبح' ? 'صبح' : 'شب';
      
      const timeoutId = await pushService.scheduleNotification(
        `${emoji} یادآوری روتین ${timeText}`,
        {
          body: `زمان روتین مراقبت پوست ${timeText} شما فرا رسیده است!`,
          data: {
            type: 'routine-reminder',
            url: '/routine',
            routineTime,
            userId,
            timestamp: Date.now(),
          },
          actions: [
            { action: 'open', title: 'مشاهده روتین', icon: '/logo_pwa.png' },
            { action: 'done', title: 'انجام شد' },
            { action: 'dismiss', title: 'بعداً' },
          ],
        },
        delay,
        'routine'
      );
      
      if (timeoutId) {
        this.scheduledNotifications.set(timeoutId, {
          type: 'routine-reminder',
          routineTime,
          userId,
          scheduledAt: Date.now(),
        });
        console.log(`✅ Routine reminder scheduled with ID: ${timeoutId}`);
      }
      
      return timeoutId;
    } catch (error) {
      console.error('❌ Failed to schedule routine reminder:', error);
      return null;
    }
  }

  /**
   * Send general reminder
   * ارسال یادآوری عمومی
   * 
   * @param {string} title - عنوان یادآوری
   * @param {string} message - متن یادآوری
   * @param {Object} options - تنظیمات اضافی
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async sendGeneralReminder(title, message, options = {}, userId = null) {
    try {
      console.log(`📢 Sending general reminder: ${title}`);
      
      const result = await pushService.showLocalNotification(
        title,
        {
          body: message,
          data: {
            type: 'general-reminder',
            url: options.url || '/',
            userId,
            timestamp: Date.now(),
            ...options.data,
          },
          actions: options.actions || [
            { action: 'open', title: 'باز کردن', icon: '/logo_pwa.png' },
            { action: 'dismiss', title: 'بستن' },
          ],
        },
        'reminder'
      );
      
      if (result) {
        console.log('✅ General reminder sent');
        this.trackNotification('general', userId);
      }
      
      return result;
    } catch (error) {
      console.error('❌ Failed to send general reminder:', error);
      return false;
    }
  }

  /**
   * Calculate delay until a specific time
   * محاسبه تأخیر تا زمان مشخص
   * 
   * @param {string} timeString - زمان به فرمت HH:MM
   * @returns {number} - تأخیر به میلی‌ثانیه
   */
  calculateDelayUntilTime(timeString) {
    try {
      const [hours, minutes] = timeString.split(':').map(Number);
      const now = new Date();
      const target = new Date();
      
      target.setHours(hours, minutes, 0, 0);
      
      // If target time has passed today, schedule for tomorrow
      if (target <= now) {
        target.setDate(target.getDate() + 1);
      }
      
      const delay = target.getTime() - now.getTime();
      console.log(`⏰ Calculated delay: ${delay}ms (${Math.round(delay / 1000 / 60)} minutes)`);
      
      return delay;
    } catch (error) {
      console.error('❌ Failed to calculate delay:', error);
      return 0;
    }
  }

  /**
   * Cancel scheduled notification
   * لغو نوتیفیکیشن برنامه‌ریزی شده
   * 
   * @param {number} notificationId - شناسه notification
   * @returns {boolean}
   */
  cancel(notificationId) {
    try {
      if (this.scheduledNotifications.has(notificationId)) {
        pushService.cancelScheduledNotification(notificationId);
        this.scheduledNotifications.delete(notificationId);
        console.log(`✅ Reminder cancelled: ${notificationId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to cancel reminder:', error);
      return false;
    }
  }

  /**
   * Cancel all scheduled reminders
   * لغو همه یادآوری‌های برنامه‌ریزی شده
   * 
   * @returns {number} - تعداد یادآوری‌های لغو شده
   */
  cancelAll() {
    try {
      let count = 0;
      for (const [notificationId] of this.scheduledNotifications) {
        if (this.cancel(notificationId)) {
          count++;
        }
      }
      console.log(`✅ Cancelled ${count} reminders`);
      return count;
    } catch (error) {
      console.error('❌ Failed to cancel all reminders:', error);
      return 0;
    }
  }

  /**
   * Track notification sent
   * ثبت نوتیفیکیشن ارسال شده
   * 
   * @param {string} type - نوع یادآوری
   * @param {string} userId - شناسه کاربر
   * @param {string} extra - اطلاعات اضافی
   */
  trackNotification(type, userId, extra = null) {
    try {
      const key = `reminder_notification_${type}_${extra || 'latest'}`;
      const data = {
        userId,
        sentAt: Date.now(),
        type,
        extra,
      };
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`📊 Tracked reminder: ${type}`);
    } catch (error) {
      console.error('❌ Failed to track notification:', error);
    }
  }
}

// Export singleton instance
export const reminderService = new ReminderService();

export default reminderService;



